import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from 'api';
import { useToast } from 'hooks/useToast';

interface UpdateStatusParams {
  assessmentId: string;
  status: 'active' | 'closed' | 'paused';
}

/**
 * Hook for updating assessment status (resume/pause/close)
 */
export const useUpdateAssessmentStatus = () => {
  const { openToast } = useToast();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ assessmentId, status }: UpdateStatusParams) => {
      const response = await apiClient.patch(
        `/assessment/jobs/${assessmentId}/`,
        { status }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      const actionText = variables.status === 'active' ? 'resumed' : 
                        variables.status === 'paused' ? 'paused' : 'closed';
      openToast({
        type: 'success',
        show: true,
        heading: 'Success',
        text: `Assessment ${actionText} successfully`,
      });
      // Invalidate and refetch jobs list
      queryClient.invalidateQueries({ queryKey: ['manageJobs'] });
      queryClient.invalidateQueries({ queryKey: ['manageJobsCounts'] });
    },
    onError: (error: any) => {
      openToast({
        type: 'error',
        show: true,
        heading: 'Error',
        text: error?.response?.data?.message || 'Failed to update assessment status',
      });
    },
  });

  return {
    updateStatus: mutation.mutate,
    isLoading: mutation.isPending,
  };
};
